/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Distributed System Agents (DSA) Schema and Types
 * 
 * DSA extends System Agents to work across distributed infrastructure
 * using a shared Git repository (the "Velocity Repo") as a coordination mechanism.
 * 
 * Key differences from System Agents:
 * - Runs are coordinated via Git (dsa/runs.md) instead of local database
 * - Agent definitions live in the Velocity Repo (dsa/agents/agents.json)
 * - Multiple Velocity instances can claim and execute runs
 * - Git handles race resolution when multiple workers try to run same agent
 * 
 * @see product/docs/VELOCITY_DSA.md for full design documentation
 */

import { z } from 'zod';

// ============================================================================
// DSA Run Status
// ============================================================================

export const DSA_RUN_STATUS = [
  'running',     // Worker is actively executing
  'completed',   // Execution finished successfully
  'failed',      // Execution failed with error
] as const;

export type DSARunStatus = typeof DSA_RUN_STATUS[number];

// ============================================================================
// DSA Run Result
// ============================================================================

export const DSA_RUN_RESULT = [
  'pass',        // No critical/high findings
  'warning',     // Medium/low findings only
  'fail',        // Critical or high findings found
] as const;

export type DSARunResult = typeof DSA_RUN_RESULT[number];

// ============================================================================
// DSA Schedule Types
// ============================================================================

export const DSA_SCHEDULE_TYPE = [
  'daily',       // Run once per day at specified time
  'weekly',      // Run once per week at specified time
  'hourly',      // Run every N hours
  'manual',      // Only run when manually triggered
] as const;

export type DSAScheduleType = typeof DSA_SCHEDULE_TYPE[number];

// ============================================================================
// DSA Priority Levels
// ============================================================================

export const DSA_PRIORITY = [
  'critical',    // Run immediately when due
  'high',        // High priority for scheduling
  'medium',      // Normal priority
  'low',         // Can be delayed if busy
] as const;

export type DSAPriority = typeof DSA_PRIORITY[number];

// ============================================================================
// DSA Agent Types
// ============================================================================

export const DSA_AGENT_TYPE = [
  'audit',       // Security, vulnerability scanning
  'measurement', // Coverage, metrics collection
  'review',      // Architecture, code quality review
  'evaluation',  // Documentation, dependencies evaluation
] as const;

export type DSAAgentType = typeof DSA_AGENT_TYPE[number];

// ============================================================================
// Velocity Repo Configuration
// ============================================================================

/**
 * Configuration for connecting to the org's Velocity Repo
 */
export interface VelocityRepoConfig {
  /** URL of the Velocity repo (e.g., https://github.com/org/velocity-repo). Optional for local-only repos. */
  repoUrl?: string;
  
  /** Git provider (github or gitlab) */
  provider: 'github' | 'gitlab';
  
  /** Branch to use (default: main) */
  branch?: string;
  
  /** Local path where repo is cloned */
  localPath?: string;
  
  /** Access token for authentication (stored securely, not in this config) */
  accessTokenRef?: string;
  
  /** Whether to use SSH instead of HTTPS */
  useSSH?: boolean;
}

// ============================================================================
// DSA Worker Configuration
// ============================================================================

/**
 * Configuration for the DSA worker running on this Velocity instance
 */
export interface DSAWorkerConfig {
  /** Unique identifier for this worker (typically machine hostname) */
  workerId: string;
  
  /** Whether DSA is enabled on this instance */
  enabled: boolean;
  
  /** Poll interval in milliseconds (default: 5 minutes = 300000) */
  pollIntervalMs: number;
  
  /** Minimum eligibility score to run an agent (0.0-1.0, default: 0.6) */
  minimumScore: number;
  
  /** Maximum concurrent runs (default: 1) */
  maxConcurrentRuns: number;
  
  /** Timeout for stale runs in milliseconds (default: 2 hours) */
  staleRunTimeoutMs: number;
  
  /** Resource limits */
  resourceLimits?: {
    maxMemoryMB?: number;
    maxCpuCores?: number;
  };
}

// ============================================================================
// DSA Agent Configuration (stored in agents.json)
// ============================================================================

/**
 * DSA-specific configuration added to standard agent definition
 * This is stored in the dsaConfig field of agents.json
 */
export interface DSAAgentConfig {
  /** Type of DSA agent (audit, measurement, review, evaluation) */
  type: DSAAgentType;
  
  /** Schedule type (daily, weekly, hourly, manual) */
  schedule: DSAScheduleType;
  
  /** Priority for scheduling */
  priority: DSAPriority;
  
  /** Timeout for agent execution (e.g., "30m", "1h") */
  timeout: string;
  
  /** Target products ("all" or specific product IDs) */
  products: 'all' | string[];
  
  /** Agent-specific configuration */
  scanTypes?: string[];
  severityThreshold?: string;
  coverageThreshold?: number;
  includeE2E?: boolean;
  patterns?: string[];
  checkDependencies?: boolean;
  autoCreateIssues?: boolean;
  
  /** Additional custom config */
  [key: string]: unknown;
}

/**
 * DSA Agent as stored in dsa/agents/agents.json
 * Extends standard agent format with DSA-specific config
 */
export interface DSAAgent {
  /** Unique name identifier */
  name: string;
  
  /** Display name for UI */
  displayName: string;
  
  /** Description of agent's purpose */
  description: string;
  
  /** Agent category (should be system_*) */
  category: string;
  
  /** Path to system prompt template (relative to dsa/agents/) */
  systemPromptTemplate: string;
  
  /** Default LLM model to use */
  defaultModel: string;
  
  /** Model configuration */
  modelConfig: {
    temperature?: number;
    maxTokens?: number;
  };
  
  /** Enabled tools for this agent */
  enabledTools: string[];
  
  /** Whether memory is enabled */
  memoryEnabled: boolean;
  
  /** Whether this is a system agent (always true for DSA) */
  isSystem: boolean;
  
  /** Whether this agent is publicly visible */
  isPublic: boolean;
  
  /** Agent status */
  status: 'active' | 'inactive';
  
  /** DSA-specific configuration */
  dsaConfig: DSAAgentConfig;
}

// ============================================================================
// DSA Run (stored in runs.md)
// ============================================================================

/**
 * A single DSA run entry as parsed from/serialized to runs.md
 */
export interface DSARun {
  /** Run ID (e.g., "RUN-20260103-001") */
  runId: string;
  
  /** Agent name that was run */
  agentName: string;
  
  /** Run status */
  status: DSARunStatus;
  
  /** Worker ID that executed the run */
  workerId: string;
  
  /** Start timestamp (ISO 8601) */
  startedAt: string;
  
  /** Completion timestamp (ISO 8601) - only present when completed/failed */
  completedAt?: string;
  
  /** Duration string (e.g., "12m 05s") - only present when completed */
  duration?: string;
  
  /** Target products (count or "all") */
  products: string;
  
  /** Number of products analyzed - only present when completed */
  productsAnalyzed?: number;
  
  /** Result summary - only present when completed */
  result?: DSARunResult;
  
  /** Result description (e.g., "PASS (2 low-severity findings)") */
  resultDescription?: string;
  
  /** Path to detailed report in dsa/results/ */
  reportPath?: string;
  
  /** Progress indicator for running tasks */
  progress?: string;
  
  /** Error message if failed */
  errorMessage?: string;
}

// ============================================================================
// DSA Schedule Entry (stored in schedule.md)
// ============================================================================

/**
 * Schedule entry as parsed from schedule.md
 */
export interface DSAScheduleEntry {
  /** Agent name */
  agentName: string;
  
  /** Cron expression (e.g., "0 9 * * *") */
  cron: string;
  
  /** Human-readable description */
  description: string;
  
  /** Target products override */
  products?: string;
  
  /** Priority override */
  priorityOverride?: DSAPriority;
}

/**
 * Global schedule configuration from schedule.md
 */
export interface DSAScheduleConfig {
  /** Global timezone setting */
  timezone: string;
  
  /** Default priority for agents */
  defaultPriority: DSAPriority;
  
  /** Max concurrent runs across all workers */
  maxConcurrentRuns: number;
  
  /** Timeout for stale runs */
  staleRunTimeout: string;
  
  /** Run history retention period */
  runHistoryRetention: string;
  
  /** Individual schedule entries */
  schedules: DSAScheduleEntry[];
}

// ============================================================================
// DSA System Configuration (stored in config.md)
// ============================================================================

/**
 * DSA system configuration from config.md
 */
export interface DSASystemConfig {
  /** Worker settings */
  worker: {
    pollInterval: string;
    maxRetries: number;
    retryBackoff: 'linear' | 'exponential';
  };
  
  /** Eligibility thresholds */
  eligibility: {
    minimumScore: number;
    priorityWeights: Record<DSAPriority, number>;
  };
  
  /** Resource limits */
  resources: {
    maxMemory: string;
    maxCpu: string;
    networkAccess: 'required' | 'optional' | 'none';
  };
  
  /** Git settings */
  git: {
    remote: string;
    branch: string;
    commitPrefix: string;
    pushRetryCount: number;
    pushRetryDelay: string;
  };
}

// ============================================================================
// Agent Eligibility Scoring
// ============================================================================

/**
 * Factors used to calculate agent eligibility score
 */
export interface ScoreFactors {
  /** Based on agent priority config (0.0-1.0) */
  priority: number;
  
  /** How overdue vs. scheduled time (0.0-1.0) */
  overdue: number;
  
  /** Time since last run of this agent (0.0-1.0) */
  recency: number;
  
  /** Worker has run this agent before (0.0-0.2) */
  workerAffinity: number;
  
  /** Worker has required resources (0.0 or 1.0) */
  resourceMatch: number;
}

/**
 * Eligibility calculation result for an agent
 */
export interface AgentEligibility {
  /** Agent name */
  agentName: string;
  
  /** Calculated eligibility score (0.0-1.0) */
  score: number;
  
  /** Whether agent is eligible to run */
  eligible: boolean;
  
  /** Score factors breakdown */
  factors: ScoreFactors;
  
  /** Last run timestamp (from runs.md history) */
  lastRun: string | null;
  
  /** Next due timestamp (calculated from schedule) */
  nextDue: string;
  
  /** Reason if not eligible */
  reason?: string;
}

// ============================================================================
// DSA Service Status
// ============================================================================

/**
 * Status of the DSA worker on this instance
 */
export interface DSAWorkerStatus {
  /** Whether DSA is enabled */
  enabled: boolean;
  
  /** Worker identifier */
  workerId: string;
  
  /** Currently executing run, if any */
  currentRun: DSARun | null;
  
  /** Timestamp of last poll cycle */
  lastPoll: string | null;
  
  /** Total runs completed by this worker */
  runsCompleted: number;
  
  /** Runs completed today */
  runsToday: number;
  
  /** Last error message, if any */
  lastError?: string;
  
  /** Velocity repo sync status */
  repoStatus: {
    synced: boolean;
    lastSync: string | null;
    error?: string;
  };
}

// ============================================================================
// API Request/Response Types
// ============================================================================

/**
 * Request to enable DSA on this instance
 */
export interface EnableDSARequest {
  /** Velocity repo URL (create new if not provided) */
  velocityRepoUrl?: string;
  
  /** Whether to create a new repo if URL not provided */
  createRepo?: boolean;
  
  /** Git provider if creating new repo */
  provider?: 'github' | 'gitlab';
  
  /** Organization name for new repo */
  orgName?: string;
}

/**
 * Request to trigger a manual DSA run
 */
export interface TriggerDSARunRequest {
  /** Specific products to target (optional) */
  products?: string[];
  
  /** Priority override */
  priority?: DSAPriority;
}

/**
 * Response for DSA run list
 */
export interface DSARunsResponse {
  /** Active (running) runs */
  active: DSARun[];
  
  /** Completed runs */
  completed: DSARun[];
}

/**
 * Response for DSA status
 */
export interface DSAStatusResponse {
  /** Worker status */
  worker: DSAWorkerStatus;
  
  /** Velocity repo configuration */
  repoConfig: VelocityRepoConfig | null;
  
  /** Available DSA agents */
  agents: DSAAgent[];
  
  /** Schedule configuration */
  schedule: DSAScheduleConfig | null;
}

// ============================================================================
// Zod Schemas for Validation
// ============================================================================

export const velocityRepoConfigSchema = z.object({
  repoUrl: z.string().url(),
  provider: z.enum(['github', 'gitlab']),
  branch: z.string().optional().default('main'),
  localPath: z.string().optional(),
  accessTokenRef: z.string().optional(),
  useSSH: z.boolean().optional().default(false),
});

export const dsaWorkerConfigSchema = z.object({
  workerId: z.string().min(1),
  enabled: z.boolean(),
  pollIntervalMs: z.number().positive().default(300000),
  minimumScore: z.number().min(0).max(1).default(0.6),
  maxConcurrentRuns: z.number().positive().default(1),
  staleRunTimeoutMs: z.number().positive().default(7200000),
  resourceLimits: z.object({
    maxMemoryMB: z.number().positive().optional(),
    maxCpuCores: z.number().positive().optional(),
  }).optional(),
});

export const dsaAgentConfigSchema = z.object({
  type: z.enum(DSA_AGENT_TYPE),
  schedule: z.enum(DSA_SCHEDULE_TYPE),
  priority: z.enum(DSA_PRIORITY),
  timeout: z.string(),
  products: z.union([z.literal('all'), z.array(z.string())]),
  scanTypes: z.array(z.string()).optional(),
  severityThreshold: z.string().optional(),
  coverageThreshold: z.number().optional(),
  includeE2E: z.boolean().optional(),
  patterns: z.array(z.string()).optional(),
  checkDependencies: z.boolean().optional(),
  autoCreateIssues: z.boolean().optional(),
}).passthrough();

export const dsaAgentSchema = z.object({
  name: z.string().min(1),
  displayName: z.string().min(1),
  description: z.string(),
  category: z.string(),
  systemPromptTemplate: z.string(),
  defaultModel: z.string(),
  modelConfig: z.object({
    temperature: z.number().optional(),
    maxTokens: z.number().optional(),
  }),
  enabledTools: z.array(z.string()),
  memoryEnabled: z.boolean(),
  isSystem: z.boolean(),
  isPublic: z.boolean(),
  status: z.enum(['active', 'inactive']),
  dsaConfig: dsaAgentConfigSchema,
});

export const dsaRunSchema = z.object({
  runId: z.string(),
  agentName: z.string(),
  status: z.enum(DSA_RUN_STATUS),
  workerId: z.string(),
  startedAt: z.string(),
  completedAt: z.string().optional(),
  duration: z.string().optional(),
  products: z.string(),
  productsAnalyzed: z.number().optional(),
  result: z.enum(DSA_RUN_RESULT).optional(),
  resultDescription: z.string().optional(),
  reportPath: z.string().optional(),
  progress: z.string().optional(),
  errorMessage: z.string().optional(),
});

export const enableDSARequestSchema = z.object({
  velocityRepoUrl: z.string().url().optional(),
  createRepo: z.boolean().optional(),
  provider: z.enum(['github', 'gitlab']).optional(),
  orgName: z.string().optional(),
});

export const triggerDSARunRequestSchema = z.object({
  products: z.array(z.string()).optional(),
  priority: z.enum(DSA_PRIORITY).optional(),
});

// ============================================================================
// Type Exports
// ============================================================================

export type VelocityRepoConfigInput = z.infer<typeof velocityRepoConfigSchema>;
export type DSAWorkerConfigInput = z.infer<typeof dsaWorkerConfigSchema>;
export type DSAAgentConfigInput = z.infer<typeof dsaAgentConfigSchema>;
export type DSAAgentInput = z.infer<typeof dsaAgentSchema>;
export type DSARunInput = z.infer<typeof dsaRunSchema>;
export type EnableDSARequestInput = z.infer<typeof enableDSARequestSchema>;
export type TriggerDSARunRequestInput = z.infer<typeof triggerDSARunRequestSchema>;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Generate a unique run ID
 */
export function generateDSARunId(): string {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `RUN-${date}-${random}`;
}

/**
 * Parse timeout string to milliseconds
 * Supports formats like "30m", "1h", "90s"
 */
export function parseTimeoutMs(timeout: string): number {
  const match = timeout.match(/^(\d+)(s|m|h)$/);
  if (!match) {
    return 30 * 60 * 1000; // Default 30 minutes
  }
  
  const value = parseInt(match[1], 10);
  const unit = match[2];
  
  switch (unit) {
    case 's': return value * 1000;
    case 'm': return value * 60 * 1000;
    case 'h': return value * 60 * 60 * 1000;
    default: return 30 * 60 * 1000;
  }
}

/**
 * Format duration in milliseconds to human-readable string
 */
export function formatDurationMs(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  if (minutes < 60) {
    return remainingSeconds > 0 
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Get priority weight for scoring
 */
export function getPriorityWeight(priority: DSAPriority): number {
  switch (priority) {
    case 'critical': return 1.0;
    case 'high': return 0.8;
    case 'medium': return 0.5;
    case 'low': return 0.3;
  }
}

/**
 * Check if an agent is due to run based on schedule
 */
export function isAgentDue(
  agentName: string,
  schedule: DSAScheduleEntry | undefined,
  lastRun: DSARun | undefined,
  now: Date = new Date()
): boolean {
  if (!schedule) return false;
  
  // Manual agents are never automatically due
  if (schedule.cron === 'manual') return false;
  
  // If never run, it's due
  if (!lastRun) return true;
  
  // Parse last run time and check against schedule
  const lastRunTime = new Date(lastRun.completedAt || lastRun.startedAt);
  const hoursSinceLastRun = (now.getTime() - lastRunTime.getTime()) / (1000 * 60 * 60);
  
  // Simple check based on schedule description
  // In production, use a proper cron parser
  if (schedule.description.includes('Daily') && hoursSinceLastRun >= 20) return true;
  if (schedule.description.includes('Weekly') && hoursSinceLastRun >= 144) return true;
  if (schedule.description.includes('Hourly') && hoursSinceLastRun >= 1) return true;
  
  return false;
}
